import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import DataTable from "@/components/DataTable";

export default function Returns() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [activeBill, setActiveBill] = useState(null);
  const [returnQty, setReturnQty] = useState({}); // index -> qty
  const [reason, setReason] = useState("");
  const [refundMethod, setRefundMethod] = useState("Cash");
  const [credits, setCredits] = useState([]);
  const [creditId, setCreditId] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const triggerReload = () => setReloadKey((k) => k + 1);

  // Only load bills for the search-by-bill dropdown — return list uses DataTable.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sales-bills");
        setBills(Array.isArray(data) ? data : (data.rows || []));
      } catch (e) {/* ignore */}
    })();
  }, []);

  const startReturn = async (bill) => {
    setActiveBill(bill);
    setReturnQty({});
    setReason(""); setRefundMethod("Cash");
    setCreditId("");
    if (bill.customer_id) {
      try {
        const { data } = await api.get(`/credits/customer/${bill.customer_id}`);
        setCredits((data.credits || []).filter((c) => c.balance > 0));
      } catch { setCredits([]); }
    } else { setCredits([]); }
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const items = Object.entries(returnQty).filter(([_, q]) => Number(q) > 0)
      .map(([idx, q]) => ({ bill_item_index: parseInt(idx), quantity: parseInt(q) }));
    if (items.length === 0) { toast.error("No items to return"); return; }
    if (refundMethod === "Credit Adjustment" && credits.length === 0) {
      toast.error("Customer has no outstanding credit to adjust against"); return;
    }
    try {
      const body = { bill_id: activeBill.id, items, reason, refund_method: refundMethod };
      if (refundMethod === "Credit Adjustment" && creditId) body.credit_id = creditId;
      const { data } = await api.post("/sales-returns", body);
      toast.success(`Credit Note ${data.credit_note_number} · ${formatINR(data.refund_total)}`);
      setOpen(false); triggerReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filteredBills = search ? bills.filter((b) =>
    [b.bill_number, b.customer_name, b.customer_phone].some((v) => (v || "").toLowerCase().includes(search.toLowerCase()))
  ).filter((b) => !b.cancelled).slice(0, 10) : [];

  return (
    <div className="space-y-6" data-testid="returns-page">
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Find original bill to return</div>
        <div className="flex gap-2">
          <Search className="w-5 h-5 text-slate-400 mt-2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Bill #, customer name, mobile..." className="rounded-none" data-testid="returns-search" />
        </div>
        {filteredBills.length > 0 && (
          <div className="mt-3 border border-slate-200 max-h-72 overflow-auto">
            {filteredBills.map((b) => (
              <button key={b.id} onClick={() => startReturn(b)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 flex justify-between text-sm" data-testid={`select-bill-${b.id}`}>
                <div>
                  <div className="font-mono text-xs">{b.bill_number}</div>
                  <div className="text-slate-600 text-xs">{b.customer_name} · {new Date(b.created_at).toLocaleDateString("en-IN")}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold tabular-nums">{formatINR(b.grand_total)}</div>
                  <div className="text-xs text-slate-500">{b.items.length} items</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto">
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs uppercase tracking-[0.2em] text-slate-500">Recent Returns / Credit Notes</div>
        <DataTable
          endpoint="/sales-returns"
          columns={[
            { key: "credit_note_number", label: "CN #", sortable: true, render: (r) => <span className="font-mono text-xs">{r.credit_note_number}</span> },
            { key: "bill_number", label: "Original Bill", render: (r) => <span className="font-mono text-xs">{r.bill_number || "—"}</span> },
            { key: "customer_name", label: "Customer", render: (r) => r.customer_name || "—" },
            { key: "reason", label: "Reason", render: (r) => <span className="text-slate-600">{r.reason || "—"}</span> },
            { key: "refund_method", label: "Mode", render: (r) => r.refund_method || r.refund_mode || "—" },
            { key: "refund_total", label: "Refund", sortable: true, className: "text-right", render: (r) => <span className="font-semibold tabular-nums">{formatINR(r.refund_total)}</span> },
            { key: "imported_from_tally", label: "Src", render: (r) => r.imported_from_tally ? <span className="text-[10px] uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">Tally</span> : <span className="text-[10px] text-slate-400">Live</span> },
          ]}
          filters={[
            { key: "refund_method", type: "select", label: "Refund", options: [{ value: "Cash", label: "Cash" }, { value: "Store Credit", label: "Store Credit" }, { value: "Credit Adjustment", label: "Credit Adjustment" }] },
            { key: "imported", type: "select", label: "Source", options: [{ value: "true", label: "Imported (Tally)" }, { value: "false", label: "Live only" }] },
            { key: "date_from", type: "date", label: "From date" },
            { key: "date_to", type: "date", label: "To date" },
          ]}
          searchPlaceholder="Search CN #, customer, bill, reason..."
          reloadKey={reloadKey}
          dataTestid="returns-table"
        />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle><RefreshCw className="w-4 h-4 mr-2 inline" />Return / Credit Note · {activeBill?.bill_number}</DialogTitle></DialogHeader>
          {activeBill && (
            <form onSubmit={submit} className="space-y-3">
              <div className="border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200"><tr><th className="p-2 text-left">Item</th><th className="p-2 text-right">Sold</th><th className="p-2 text-right">Already Returned</th><th className="p-2 text-right">Return Qty</th></tr></thead>
                  <tbody>
                    {activeBill.items.map((it, i) => {
                      const max = it.quantity - (it.returned_qty || 0);
                      return (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="p-2"><div className="font-medium">{it.name}</div><div className="text-xs text-slate-500 font-mono">{it.barcode}</div></td>
                          <td className="p-2 text-right tabular-nums">{it.quantity}</td>
                          <td className="p-2 text-right tabular-nums">{it.returned_qty || 0}</td>
                          <td className="p-2 text-right">
                            <Input type="number" min={0} max={max} value={returnQty[i] || 0} onChange={(e) => setReturnQty({ ...returnQty, [i]: e.target.value })} className="rounded-none w-20 h-8 ml-auto" data-testid={`return-qty-${i}`} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Reason</Label><Input required value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-none mt-1" data-testid="return-reason" /></div>
                <div><Label>Refund Method</Label>
                  <Select value={refundMethod} onValueChange={setRefundMethod}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="return-refund-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash Return</SelectItem>
                      <SelectItem value="Store Credit">Store Credit</SelectItem>
                      <SelectItem value="Credit Adjustment" disabled={credits.length === 0}>
                        Credit Adjustment {credits.length === 0 ? "(no outstanding)" : `(${credits.length})`}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {refundMethod === "Credit Adjustment" && credits.length > 0 && (
                <div><Label>Apply to which credit entry?</Label>
                  <Select value={creditId} onValueChange={setCreditId}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="return-credit-id"><SelectValue placeholder="Auto: oldest first" /></SelectTrigger>
                    <SelectContent>
                      {credits.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.bill_number} · Balance {formatINR(c.balance)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="submit-return">Issue Credit Note</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
